import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Pagination from '../common/Pagination/Pagination';
import { useDataEntries } from '../../queries/dataEntryQueries';
import { useCustomers } from '../../queries/customerQueries';
import { useDeleteDataEntry } from '../../mutations/dataEntryMutations';

const ITEMS_PER_PAGE = 8;

const DataManagement = () => {
    const navigate = useNavigate();

    // ─── Role Restrictions ────────────────────────────────────────────────────
    const isImporter = sessionStorage.getItem('importer') === 'true';
    const userRole = sessionStorage.getItem('userRole') ?? '';
    const isLimitedAdmin = userRole.trim().toLowerCase() === 'limited admin';
    // Both Importer login and Limited Admin normal login share the same UI restrictions
    const isRestricted = isImporter || isLimitedAdmin;

    // ─── Local State ──────────────────────────────────────────────────────────
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedExporter, setSelectedExporter] = useState('');
    const [selectedImporter, setSelectedImporter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRowIds, setSelectedRowIds] = useState(new Set());

    // ─── API Data ─────────────────────────────────────────────────────────────
    const { data: apiData, isLoading, isError } = useDataEntries(currentPage, searchTerm);
    const { data: apiCustomers } = useCustomers();
    const deleteMutation = useDeleteDataEntry();

    // Extract records array from API envelope
    const dataEntries = useMemo(() => {
        if (!apiData) return [];
        if (Array.isArray(apiData)) return apiData;
        if (Array.isArray(apiData.data)) return apiData.data;
        if (Array.isArray(apiData.items)) return apiData.items;
        return [];
    }, [apiData]);

    const totalPages = useMemo(() => {
        if (apiData?.totalPages) return apiData.totalPages;
        if (apiData?.totalRecords) return Math.ceil(apiData.totalRecords / ITEMS_PER_PAGE);
        if (apiData?.total) return Math.ceil(apiData.total / ITEMS_PER_PAGE);
        return Math.ceil(dataEntries.length / ITEMS_PER_PAGE) || 1;
    }, [apiData, dataEntries]);

    // ─── Exporter & Importer Options ──────────────────────────────────────────
    const exporterOptions = useMemo(() => {
        const exporters = new Set();
        (dataEntries || []).forEach((item) => {
            if (item?.exporter && item.exporter !== '-') exporters.add(item.exporter);
        });
        return ['', ...Array.from(exporters)];
    }, [dataEntries]);

    const importerOptions = useMemo(() => {
        const importers = new Set();
        (dataEntries || []).forEach((item) => {
            if (item?.importer && item.importer !== '-') importers.add(item.importer);
            if (Array.isArray(item?.allowedImporters)) {
                item.allowedImporters.forEach((imp) => imp && importers.add(imp));
            }
        });
        const rawCustomers = apiCustomers?.data || apiCustomers?.items || (Array.isArray(apiCustomers) ? apiCustomers : []);
        (rawCustomers || []).forEach((c) => {
            const name = c?.customerName || c?.companyName || c?.name;
            if (name) importers.add(name);
        });
        return ['', ...Array.from(importers).sort()];
    }, [dataEntries, apiCustomers]);

    const filteredEntries = useMemo(() => {
        return (dataEntries || []).filter((item) => {
            const matchesExporter = !selectedExporter || item?.exporter === selectedExporter;
            const itemImporters = Array.isArray(item?.allowedImporters)
                ? item.allowedImporters
                : item?.importer
                ? [item.importer]
                : [];
            const matchesImporter =
                !selectedImporter ||
                item?.selectAllImporters ||
                itemImporters.includes(selectedImporter) ||
                item?.importer === selectedImporter;
            return matchesExporter && matchesImporter;
        });
    }, [dataEntries, selectedExporter, selectedImporter]);

    // Paginate client-side only if the API returned a plain array (no server pagination envelope)
    const paginatedEntries = useMemo(() => {
        if (apiData?.items || apiData?.data) return filteredEntries; // server already paginated
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredEntries.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredEntries, currentPage, apiData]);

    // ─── Row Selection ────────────────────────────────────────────────────────
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = paginatedEntries.map((c) => c.id);
            setSelectedRowIds(new Set([...selectedRowIds, ...allIds]));
        } else {
            const newSelected = new Set(selectedRowIds);
            paginatedEntries.forEach((c) => newSelected.delete(c.id));
            setSelectedRowIds(newSelected);
        }
    };

    const handleSelectRow = (id) => {
        const newSelected = new Set(selectedRowIds);
        if (newSelected.has(id)) newSelected.delete(id);
        else newSelected.add(id);
        setSelectedRowIds(newSelected);
    };

    const isAllSelectedOnPage = useMemo(() => {
        if (paginatedEntries.length === 0) return false;
        return paginatedEntries.every((c) => selectedRowIds.has(c.id));
    }, [paginatedEntries, selectedRowIds]);

    // ─── Delete selected ──────────────────────────────────────────────────────
    const handleDeleteSelected = () => {
        const idsToDelete = Array.from(selectedRowIds);
        if (idsToDelete.length === 0) return;

        Promise.all(idsToDelete.map((id) => deleteMutation.mutateAsync(id)))
            .then(() => {
                setSelectedRowIds(new Set());
            })
            .catch(() => {
                // Toastify error notification is handled by mutation onError
            });
    };

    // ─── Format Helpers ───────────────────────────────────────────────────────
    const formatDate = (dateVal) => {
        if (!dateVal) return '';
        const m = moment(dateVal);
        return m.isValid() ? m.format('DD-MM-YYYY') : dateVal;
    };

    const formatNoAndDate = (num, dateVal) => {
        const n = num || '-';
        const formattedDate = formatDate(dateVal);
        if (!formattedDate || n === '-') return n;
        return (
            <div className="d-flex flex-column align-items-start lh-sm">
                <span>{n}</span>
                <span className="text-muted small">{formattedDate}</span>
            </div>
        );
    };

    const formatEta = (row) => {
        const eta = row.etaDate || row.eta;
        if (!eta) {
            const rd = row.remainingDays;
            if (rd === undefined || rd === null || rd === '') return '-';
            if (typeof rd === 'string' && rd.toLowerCase().includes('day')) return rd;
            return `${rd} ${Number(rd) === 1 ? 'day' : 'days'}`;
        }

        const etaMoment = moment(eta).startOf('day');
        const todayMoment = moment().startOf('day');

        if (!etaMoment.isValid()) return eta;

        const diffDays = etaMoment.diff(todayMoment, 'days');
        const absDays = Math.abs(diffDays);
        const dayWord = absDays === 1 ? 'day' : 'days';
        const sign = diffDays < 0 ? '-' : '';
        const daysText = `${sign}${absDays} ${dayWord}`;
        return daysText;
    };


    return (
        <div className="d-flex flex-column w-100 h-100">
            {/* ══ Enterprise Toolbar ══ */}
            <div className="gtss-toolbar-strip">
                {/* ── Left: Module Title ── */}
                <div className="d-flex flex-column me-1" style={{ flexShrink: 0 }}>
                    <span className="gtss-page-title">Data Management</span>
                    <span className="gtss-page-subtitle">Entry &amp; Operations Registry</span>
                </div>

                {/* ── Divider ── */}
                <div className="gtss-divider"></div>

                {/* ── Search ── */}
                <div className="input-group" style={{ width: '240px' }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search BL No, File Name..."
                        aria-label="Search by BL No or File Name"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                    <span className="input-group-text">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
                        </svg>
                    </span>
                </div>

                {/* ── Divider ── */}
                <div className="gtss-divider"></div>

                {/* ── Filters ── */}
                <div className="d-flex gap-2 align-items-center">
                    <select
                        className="form-select form-select-dark"
                        aria-label="Filter by Exporter"
                        value={selectedExporter}
                        onChange={(e) => { setSelectedExporter(e.target.value); setCurrentPage(1); }}
                        style={{ minWidth: '130px', cursor: isRestricted ? 'not-allowed' : 'pointer' }}
                        disabled={isRestricted}
                    >
                        <option value="">All Exporters</option>
                        {exporterOptions.filter(Boolean).map((exp) => (
                            <option key={exp} value={exp}>{exp}</option>
                        ))}
                    </select>

                    <select
                        className="form-select form-select-dark"
                        aria-label="Filter by Importer"
                        value={selectedImporter}
                        onChange={(e) => { setSelectedImporter(e.target.value); setCurrentPage(1); }}
                        style={{ minWidth: '130px', cursor: isRestricted ? 'not-allowed' : 'pointer' }}
                        disabled={isRestricted}
                    >
                        <option value="">All Importers</option>
                        {importerOptions.filter(Boolean).map((imp) => (
                            <option key={imp} value={imp}>{imp}</option>
                        ))}
                    </select>
                </div>

                {/* ── Right Action Buttons ── */}
                <div className="d-flex align-items-center gap-2 ms-auto">
                    {selectedRowIds.size > 0 && (
                        <button
                            className="btn btn-danger btn-sm fw-semibold text-uppercase"
                            onClick={handleDeleteSelected}
                            disabled={deleteMutation.isPending || isRestricted}
                            style={{ cursor: isRestricted ? 'not-allowed' : 'pointer' }}
                        >
                            {deleteMutation.isPending ? 'Deleting...' : `Delete (${selectedRowIds.size})`}
                        </button>
                    )}

                    <button
                        className="btn btn-orange text-uppercase d-flex align-items-center gap-1"
                        onClick={() => navigate('/business/data-entry/new')}
                        aria-label="Create new data entry"
                        disabled={isImporter}
                        style={{ cursor: isImporter ? 'not-allowed' : 'pointer' }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
                        </svg>
                        New Data Entry
                    </button>
                </div>
            </div>

            {/* ── Table Container ── */}
            <div className="flex-grow-1 overflow-hidden d-flex flex-column gtss-view-container">
                <div className="border rounded-3 overflow-hidden bg-white d-flex flex-column flex-grow-1">
                    <div className="table-responsive flex-grow-1">
                        <table className="table table-hover align-middle mb-0 gtss-table text-nowrap data-management-table">
                            <thead className="gtss-bg-primary text-white text-uppercase">
                                <tr>
                                    <th className="py-2 px-2 text-center" style={{ width: '100px' }}>
                                        SELECT/EDIT
                                    </th>
                                    <th className="py-2 text-start" style={{ width: '65px' }}>File No</th>
                                    <th className="py-2 text-start" style={{ width: '85px' }}>ETA</th>
                                    <th className="py-2 text-start" style={{ minWidth: '90px', maxWidth: '110px', whiteSpace: 'normal' }}>BL No &amp; Date</th>
                                    <th className="py-2 text-start" style={{ maxWidth: '110px', whiteSpace: 'normal' }}>Exporter</th>
                                    <th className="py-2 text-start" style={{ maxWidth: '110px', whiteSpace: 'normal' }}>Importer</th>
                                    <th className="py-2 text-start" style={{ minWidth: '90px', maxWidth: '110px', whiteSpace: 'normal' }}>INV NO &amp; DATE</th>
                                    <th className="py-2 text-start" style={{ minWidth: '90px', maxWidth: '110px', whiteSpace: 'normal' }}>BE No &amp; Date</th>
                                    <th className="py-2 text-start">DA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="9" className="py-5 text-center text-muted">
                                            <div className="spinner-border spinner-border-sm me-2" role="status" />
                                            Loading data entries...
                                        </td>
                                    </tr>
                                ) : paginatedEntries.length > 0 ? (
                                    paginatedEntries.map((row) => {
                                        const isSelected = selectedRowIds.has(row.id);
                                        return (
                                            <tr
                                                key={row.id}
                                                className={isSelected ? 'table-primary-subtle' : ''}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleSelectRow(row.id)}
                                            >
                                                <td className="px-2 text-center" onClick={(e) => e.stopPropagation()}>
                                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                                        {/* Checkbox — cursor:not-allowed via wrapper span */}
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                cursor: isRestricted ? 'not-allowed' : 'auto',
                                                            }}
                                                            title={isRestricted ? 'You are not allowed to access' : undefined}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input mt-0"
                                                                aria-label="Select row"
                                                                checked={isSelected}
                                                                onChange={() => handleSelectRow(row.id)}
                                                                disabled={isRestricted}
                                                                style={{
                                                                    width: '14px',
                                                                    height: '14px',
                                                                    cursor: isRestricted ? 'not-allowed' : 'pointer',
                                                                    pointerEvents: isRestricted ? 'none' : 'auto',
                                                                }}
                                                            />
                                                        </span>

                                                        {/* Edit — always enabled for all roles */}
                                                        <button
                                                            className="btn p-0 border-0 bg-transparent dm-icon-btn"
                                                            onClick={() => navigate(`/business/data-entry/${row.id}`)}
                                                            title="Edit"
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                                <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z"/>
                                                            </svg>
                                                        </button>

                                                        {/* View icon — cursor:not-allowed via wrapper span */}
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                cursor: isRestricted ? 'not-allowed' : 'auto',
                                                            }}
                                                            title={isRestricted ? 'You are not allowed to access' : undefined}
                                                        >
                                                            <button
                                                                className="btn p-0 border-0 bg-transparent dm-icon-btn dm-icon-btn--view"
                                                                onClick={() => {
                                                                    const getContainerArray = (r) => {
                                                                        if (Array.isArray(r.containers) && r.containers.length > 0) {
                                                                            return r.containers.map((c) => (typeof c === 'object' ? c.containerNo || c.container_no : c)).filter(Boolean);
                                                                        }
                                                                        if (Array.isArray(r.userForm) && r.userForm.length > 0) {
                                                                            return r.userForm.map((c) => c.containerNo || c.container_no).filter(Boolean);
                                                                        }
                                                                        if (Array.isArray(r.dataEntryForm) && r.dataEntryForm.length > 0) {
                                                                            return r.dataEntryForm.map((c) => c.containerNo || c.container_no).filter(Boolean);
                                                                        }
                                                                        const rawStr = r.containerNo || r.containersNumber || r.containerNos || r.containerNumber || r.container_no || '';
                                                                        if (typeof rawStr === 'string' && rawStr.trim()) {
                                                                            return rawStr.split(',').map((s) => s.trim()).filter(Boolean);
                                                                        }
                                                                        return [];
                                                                    };
                                                                    const containerList = getContainerArray(row);
                                                                    const uniqueContainers = Array.from(new Set(containerList));
                                                                    navigate(`/business/data-upload/${row.id}`, {
                                                                        state: {
                                                                            fileName: row.fileName,
                                                                            blNo: row.blNo || row.billno,
                                                                            containerList,
                                                                            containerNo: uniqueContainers.join(', '),
                                                                        },
                                                                    });
                                                                }}
                                                                title="View Documents"
                                                                disabled={isRestricted}
                                                                style={{
                                                                    cursor: isRestricted ? 'not-allowed' : 'pointer',
                                                                    pointerEvents: isRestricted ? 'none' : 'auto',
                                                                }}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                                    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z"/>
                                                                    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0"/>
                                                                </svg>
                                                            </button>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="fw-semibold text-start text-primary">{row.fileName || '-'}</td>
                                                <td className="text-start font-monospace fw-bold">{formatEta(row)}</td>
                                                <td className="text-start" style={{ minWidth: '90px', maxWidth: '110px', whiteSpace: 'normal' }}>{formatNoAndDate(row.blNo || row.billno, row.dateOfShipping)}</td>
                                                <td className="text-start" style={{ maxWidth: '110px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.exporter || '-'}</td>
                                                <td className="text-start" style={{ maxWidth: '110px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.importer || '-'}</td>
                                                <td className="text-start" style={{ minWidth: '90px', maxWidth: '110px', whiteSpace: 'normal' }}>{formatNoAndDate(row.invoiceNo || row.invoiceno, row.invoiceDt || row.invoicedt)}</td>
                                                <td className="text-start" style={{ minWidth: '90px', maxWidth: '110px', whiteSpace: 'normal' }}>{formatNoAndDate(row.beNo, row.beDate)}</td>
                                                <td className="text-start">{!row.paidandunpaid && row.roundOff ? row.roundOff : '-'}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="py-4 text-center text-muted">
                                            No data management records found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    {totalPages >= 1 && (
                        <div className="border-top bg-white py-3 px-4 d-flex justify-content-end">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DataManagement;